import React from 'react';
import { bool, func, number, object, string } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { INVOICING_ACCOUNT } from '../../util/invoicing';
import { propTypes } from '../../util/types';
import {
  InlineTextButton,
  InvoicingStatusBox,
  PrimaryButton,
  SecondaryButton,
} from '../../components';
import { RequestInvoicingForm } from '../../forms';

import css from './BillingPage.css';

// Section 2's invoice block (spec §1 state table and §4). The parent owns the
// "request form open" flag so the page's deep link can open it.
const InvoiceSection = props => {
  const {
    intl,
    state,
    profileComplete,
    billing,
    invoicing,
    currentUser,
    paymentTermDays,
    requestOpen,
    onToggleRequest,
    onEditBilling,
    onContactSupport,
    onLookupCompany,
    lookupInProgress,
    lookupResult,
    lookupError,
    onSubmitRequest,
    requestInProgress,
    requestError,
    requestResult,
  } = props;

  if (!profileComplete) {
    return (
      <p className={css.muted}>
        <FormattedMessage id="BillingPage.completeBillingFirst" />
      </p>
    );
  }

  // A fresh outcome shows at once; the refetched user catches up behind it.
  const freshResult = requestResult && !requestInProgress ? requestResult : null;
  const shown = freshResult ? freshResult.status : state;
  const record = invoicing || {};
  const companyName =
    (freshResult && freshResult.company && freshResult.company.name) ||
    record.companyName ||
    (record.billing && record.billing.legalName) ||
    billing.legalName;
  const businessId =
    (freshResult && freshResult.company && freshResult.company.businessId) || record.businessId;

  const meta = (
    <p className={css.statusMeta}>
      {[companyName, businessId].filter(Boolean).join(' · ')}
      {record.requestedAt ? (
        <>
          {' · '}
          <FormattedMessage
            id="InvoicingStatusBox.requestSent"
            values={{
              date: intl.formatDate(new Date(record.requestedAt), {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
              }),
            }}
          />
        </>
      ) : null}
    </p>
  );

  const form = (
    <RequestInvoicingForm
      formId="BillingPageRequestInvoicing"
      className={css.inlineRequestForm}
      billing={billing}
      currentUser={currentUser}
      onLookupCompany={onLookupCompany}
      lookupInProgress={lookupInProgress}
      lookupResult={lookupResult}
      lookupError={lookupError}
      onSubmit={onSubmitRequest}
      submitInProgress={requestInProgress}
      submitError={requestError}
      onEditBilling={onEditBilling}
      onCancel={() => onToggleRequest(false)}
    />
  );

  switch (shown) {
    case INVOICING_ACCOUNT.NOT_BUSINESS:
      return (
        <p className={css.muted}>
          <FormattedMessage
            id="BillingPage.invoiceConsumerHint"
            values={{ b: chunks => <b>{chunks}</b> }}
          />
        </p>
      );

    case INVOICING_ACCOUNT.PENDING:
      return (
        <InvoicingStatusBox
          status="pending"
          title={<FormattedMessage id="InvoicingStatusBox.pendingTitle" />}
          text={<FormattedMessage id="InvoicingStatusBox.pendingText" />}
        >
          {meta}
        </InvoicingStatusBox>
      );

    case INVOICING_ACCOUNT.DENIED: {
      // A denied company can ask again; a profile switched to "Me personally"
      // in the meantime cannot, since the request would fail server-side.
      const isBusiness = billing && billing.customerType === 'BUSINESS';
      return (
        <div>
          <InvoicingStatusBox
            status="denied"
            title={<FormattedMessage id="InvoicingStatusBox.deniedTitle" />}
            text={<FormattedMessage id="InvoicingStatusBox.deniedText" />}
          >
            {meta}
            <div className={css.statusActions}>
              {isBusiness && !requestOpen ? (
                <SecondaryButton
                  type="button"
                  className={css.inlineButton}
                  onClick={() => onToggleRequest(true)}
                >
                  <FormattedMessage id="BillingPage.requestAgain" />
                </SecondaryButton>
              ) : null}
              <InlineTextButton type="button" onClick={onContactSupport}>
                <FormattedMessage id="InvoicingStatusBox.contactSupport" />
              </InlineTextButton>
            </div>
          </InvoicingStatusBox>
          {isBusiness ? null : (
            <p className={css.muted}>
              <FormattedMessage
                id="BillingPage.invoiceConsumerHint"
                values={{ b: chunks => <b>{chunks}</b> }}
              />
            </p>
          )}
          {isBusiness && requestOpen ? <div className={css.requestAfterDenied}>{form}</div> : null}
        </div>
      );
    }

    case INVOICING_ACCOUNT.APPROVED: {
      // The legacy box (no company, just the account email) is only for a
      // customer with no request on file at all. A request made from this
      // page comes back approved before the refetched user lands, so
      // freshResult alone must also keep us out of that branch.
      if (!invoicing && !freshResult) {
        const email = currentUser && currentUser.attributes ? currentUser.attributes.email : '';
        return (
          <InvoicingStatusBox
            status="approved"
            title={<FormattedMessage id="InvoicingStatusBox.approvedLegacyTitle" />}
            text={
              <FormattedMessage
                id="InvoicingStatusBox.legacyText"
                values={{ days: paymentTermDays, email }}
              />
            }
            onContactSupport={onContactSupport}
          />
        );
      }
      const delivery = (invoicing && invoicing.billing) || {};
      return (
        <InvoicingStatusBox
          status="approved"
          title={
            <FormattedMessage id="InvoicingStatusBox.approvedTitle" values={{ companyName }} />
          }
          text={
            <FormattedMessage
              id="InvoicingStatusBox.paymentTerm"
              values={{ days: paymentTermDays }}
            />
          }
          onContactSupport={onContactSupport}
        >
          <ul className={css.statusRows}>
            {delivery.email ? (
              <li>
                <FormattedMessage
                  id="InvoicingStatusBox.deliveryEmail"
                  values={{ email: delivery.email }}
                />
              </li>
            ) : null}
            {delivery.eInvoiceAddress ? (
              <li>
                <FormattedMessage
                  id={
                    delivery.eInvoiceOperator
                      ? 'InvoicingStatusBox.eInvoice'
                      : 'InvoicingStatusBox.eInvoiceNoOperator'
                  }
                  values={{
                    address: delivery.eInvoiceAddress,
                    operator: delivery.eInvoiceOperator,
                  }}
                />
              </li>
            ) : null}
            {delivery.contactPerson ? (
              <li>
                <FormattedMessage
                  id="InvoicingStatusBox.contactPerson"
                  values={{ name: delivery.contactPerson }}
                />
              </li>
            ) : null}
          </ul>
          <p className={css.statusMeta}>
            <FormattedMessage id="InvoicingStatusBox.changeViaSupport" />
          </p>
        </InvoicingStatusBox>
      );
    }

    case INVOICING_ACCOUNT.ELIGIBLE:
    default: {
      // Not reachable for a consumer profile in practice (ELIGIBLE implies a
      // business), but guarded the same way so the form is never offered.
      if (!(billing && billing.customerType === 'BUSINESS')) {
        return (
          <p className={css.muted}>
            <FormattedMessage
              id="BillingPage.invoiceConsumerHint"
              values={{ b: chunks => <b>{chunks}</b> }}
            />
          </p>
        );
      }
      return (
        <div className={css.invoiceBox}>
          <p className={css.invoiceText}>
            <FormattedMessage id="BillingPage.invoiceIntro" values={{ days: paymentTermDays }} />
          </p>
          {requestOpen ? (
            form
          ) : (
            <div>
              <PrimaryButton
                type="button"
                className={css.inlineButton}
                onClick={() => onToggleRequest(true)}
              >
                <FormattedMessage id="BillingPage.requestInvoicing" />
              </PrimaryButton>
            </div>
          )}
        </div>
      );
    }
  }
};

InvoiceSection.defaultProps = {
  billing: null,
  invoicing: null,
  currentUser: null,
  requestOpen: false,
  lookupInProgress: false,
  lookupResult: null,
  lookupError: null,
  requestInProgress: false,
  requestError: null,
  requestResult: null,
};

InvoiceSection.propTypes = {
  intl: intlShape.isRequired,
  state: string.isRequired,
  profileComplete: bool.isRequired,
  billing: object,
  invoicing: object,
  currentUser: propTypes.currentUser,
  paymentTermDays: number.isRequired,
  requestOpen: bool,
  onToggleRequest: func.isRequired,
  onEditBilling: func.isRequired,
  onContactSupport: func.isRequired,
  onLookupCompany: func.isRequired,
  lookupInProgress: bool,
  lookupResult: object,
  lookupError: propTypes.error,
  onSubmitRequest: func.isRequired,
  requestInProgress: bool,
  requestError: propTypes.error,
  requestResult: object,
};

export default InvoiceSection;
